import React, { useState, useContext, useEffect } from 'react';
import { LocaleContext, AuthContext } from '../locale';
import './AdminAddProduct.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

export default function AdminAddProduct() {
  const { t } = useContext(LocaleContext);
  const { token, user, openAuth } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    category: 'beer',
    marque: '',
    type: '',
    color: '',
    country: '',
    gamme: 'Standard',
    container: 'Bottle',
    price: '',
    volume: '',
    abv: '',
    image_url: '',
    description: '',
    stock_quantity: '0'
  });

  // Protect: redirect non-admins
  useEffect(() => {
    if (!user) {
      openAuth && openAuth('login');
      window.location.hash = '#/';
    } else if (user.role !== 'admin') {
      window.location.hash = '#/';
    }
  }, [user, openAuth]);

  if (!user || user.role !== 'admin') return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update image preview when image_url changes
    if (name === 'image_url') {
      setImagePreview(value);
    }
  };

  const generateId = () => {
    const name = formData.name.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    const marque = formData.marque.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    return `${name}_${marque}_${Date.now()}`.substring(0, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Generate ID if not provided
      const productId = formData.id || generateId();
      
      const response = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          id: productId,
          price: parseFloat(formData.price),
          abv: parseFloat(formData.abv),
          stock_quantity: parseInt(formData.stock_quantity)
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Produit ajouté avec succès!');
        // Reset form
        setFormData({
          id: '',
          name: '',
          category: 'beer',
          marque: '',
          type: '',
          color: '',
          country: '',
          gamme: 'Standard',
          container: 'Bottle',
          price: '',
          volume: '',
          abv: '',
          image_url: '',
          description: '',
          stock_quantity: '0'
        });
      } else {
        setError(data.message || 'Erreur lors de l\'ajout du produit');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="admin-add-product">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <button 
              className="back-btn"
              onClick={() => window.location.hash = '#/admin'}
              title="Retour au dashboard"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <div className="header-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            </div>
            <div className="header-text">
              <h1 className="h1">Ajouter un Produit</h1>
              <p className="page-subtitle">Ajoutez un nouveau produit au catalogue</p>
            </div>
          </div>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit} className="product-form">
            {success && <div className="alert success">{success}</div>}
            {error && <div className="alert error">{error}</div>}

            <div className="form-section">
              <h3>Informations de base</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Nom du produit *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Heineken"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="id">ID du produit</label>
                  <input
                    type="text"
                    id="id"
                    name="id"
                    value={formData.id}
                    onChange={handleInputChange}
                    placeholder="Laissez vide pour génération automatique"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">Catégorie *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="beer">Bière</option>
                    <option value="wine">Vin</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="marque">Marque *</label>
                  <input
                    type="text"
                    id="marque"
                    name="marque"
                    value={formData.marque}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Heineken"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Détails du produit</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="type">Type *</label>
                  <input
                    type="text"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Lager, Pilsner, Rouge, Blanc"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="color">Couleur *</label>
                  <input
                    type="text"
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: Blonde, Brune, Rouge, Blanc"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">Pays *</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: France, Germany, Italy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="gamme">Gamme *</label>
                  <select
                    id="gamme"
                    name="gamme"
                    value={formData.gamme}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Standard">Standard</option>
                    <option value="Premium">Premium</option>
                    <option value="Luxury">Luxury</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Spécifications</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="container">Contenant *</label>
                  <select
                    id="container"
                    name="container"
                    value={formData.container}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Bottle">Bouteille</option>
                    <option value="Can">Canette</option>
                    <option value="Keg">Fût</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="volume">Volume *</label>
                  <input
                    type="text"
                    id="volume"
                    name="volume"
                    value={formData.volume}
                    onChange={handleInputChange}
                    required
                    placeholder="Ex: 33cl, 75cl, 1L"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="abv">Degré d'alcool (%) *</label>
                  <input
                    type="number"
                    id="abv"
                    name="abv"
                    value={formData.abv}
                    onChange={handleInputChange}
                    required
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="Ex: 5.0, 12.5"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price">Prix (€) *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    placeholder="Ex: 3.50, 15.99"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Média et stock</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="image_url">URL de l'image</label>
                  <input
                    type="url"
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                  />
                  <small style={{color: 'var(--muted)', fontSize: '12px', marginTop: '4px'}}>
                    Utilisez un lien direct vers une image (jpg, png, webp). Taille recommandée: 400x400px
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="stock_quantity">Quantité en stock</label>
                  <input
                    type="number"
                    id="stock_quantity"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="Ex: 100"
                  />
                </div>
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div className="image-preview">
                  <label>Aperçu de l'image:</label>
                  <div className="preview-container">
                    <img 
                      src={imagePreview} 
                      alt="Aperçu du produit"
                      className="preview-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="preview-error" style={{display: 'none'}}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                      <span>Image non trouvée</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group full-width">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Description détaillée du produit..."
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn primary" disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner" />
                    Ajout en cours...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: 8}}>
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="16"/>
                      <line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                    Ajouter le produit
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
